/**
 * Sends contact messages and game feedback to POST /api/feedback
 * (worker/index.ts). Everything is checked here first with the same validator
 * the Worker uses, so a mistake is caught before anything is sent.
 */

import { HONEYPOT_FIELD, parseSubmission, type Submission, type SubmissionField } from './submission';

export class SubmitError extends Error {
  /** The field to point at, when the problem is with one. */
  readonly field?: SubmissionField;

  constructor(message: string, field?: SubmissionField) {
    super(message);
    this.name = 'SubmitError';
    this.field = field;
  }
}

const OFFLINE_MESSAGE = 'Could not send. Check your connection and try again.';

/** A fresh id for a new submission. Sending the same id again updates it. */
export function newSubmissionId(): string {
  return crypto.randomUUID();
}

/** The slug of the game on this page (/games/<slug>), if it is a game page. */
export function currentGameSlug(): string | undefined {
  return /^\/games\/([a-z0-9-]+?)(?:\.html)?\/?$/.exec(window.location.pathname)?.[1];
}

/**
 * Checks and sends a submission. Rejects with a SubmitError whose message can
 * be shown as it is. `honeypot` is the hidden field's value, sent as typed.
 */
export async function submit(submission: Submission, honeypot = ''): Promise<void> {
  const parsed = parseSubmission(submission);
  if (!parsed.ok) throw new SubmitError(parsed.error, parsed.field);

  let response: Response;
  try {
    response = await fetch('/api/feedback', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...parsed.submission, [HONEYPOT_FIELD]: honeypot }),
    });
  } catch {
    throw new SubmitError(OFFLINE_MESSAGE);
  }
  if (response.ok) return;

  const body: unknown = await response.json().catch(() => null);
  const error = typeof body === 'object' && body !== null && 'error' in body ? body.error : null;
  throw new SubmitError(typeof error === 'string' ? error : OFFLINE_MESSAGE);
}
